// Push entry points — architecture §3.7, App Flow §6. Browsers only allow the permission
// prompt from a tap, so Home offers it once (dismissible) and Profile always has the switch.
import { Bell, Button, Card, Switch, X, useToast } from '@roots/ui';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { type PushState, usePush } from '../lib/push.js';

const DISMISS_KEY = 'roots:push-card-dismissed';

const STATE_HINT: Partial<Record<PushState, string>> = {
  'needs-install':
    'On iPhone and iPad, add Roots to your Home Screen first, then turn this on there.',
  denied:
    'Notifications are blocked for Roots in your browser settings. Allow them there, then come back.',
  unsupported: 'This browser can’t show notifications. Your inbox on Home still has everything.',
};

/** Home: shown only while push is off and the browser hasn't been asked yet. */
export function PushPromptCard() {
  const { state, busy, enable } = usePush();
  const { push } = useToast();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const askable =
    state === 'off' && typeof Notification !== 'undefined' && Notification.permission === 'default';
  if (dismissed || !askable) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {}
  };

  return (
    <Card className="relative flex flex-col gap-3">
      <button
        type="button"
        aria-label="Not now"
        onClick={dismiss}
        className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center text-[var(--color-text-secondary)]"
      >
        <X size={18} aria-hidden />
      </button>
      <p className="type-subheading flex items-center gap-2 pr-10">
        <Bell size={20} aria-hidden /> Get a nudge on drop day
      </p>
      <p className="type-body">
        We’ll let you know when prompts open, when your family’s edition arrives, and when a
        birthday is coming up. You can change this any time in Profile.
      </p>
      <Button
        className="self-start"
        loading={busy}
        onClick={async () => {
          try {
            const next = await enable();
            if (next === 'on') push('Notifications are on.');
            else if (next === 'denied') dismiss();
          } catch {
            push('We couldn’t turn notifications on. Try again in a moment.', 'error');
          }
        }}
      >
        Turn on notifications
      </Button>
    </Card>
  );
}

/** Profile → Settings: the switch, why it may be unavailable, and a test send. */
export function PushSettings() {
  const { state, busy, enable, disable } = usePush();
  const { push } = useToast();
  const [testing, setTesting] = useState(false);
  const hint = STATE_HINT[state];
  const available = state === 'on' || state === 'off';

  return (
    <div className="flex flex-col gap-2">
      <Switch
        label="Push notifications"
        checked={state === 'on'}
        disabled={!available || busy}
        onCheckedChange={async (on) => {
          try {
            const next = await (on ? enable() : disable());
            if (on && next === 'on') push('Notifications are on.');
            if (!on) push('Notifications are off on this device.');
          } catch {
            push('We couldn’t change notifications. Try again in a moment.', 'error');
          }
        }}
      />
      {hint && <p className="type-caption">{hint}</p>}
      {state === 'on' && (
        <Button
          variant="ghost"
          className="self-start"
          loading={testing}
          onClick={async () => {
            setTesting(true);
            try {
              const result = await api.testPush();
              push(
                result.delivered > 0
                  ? 'Test sent. It should appear in a few seconds.'
                  : 'The test didn’t reach this device. Turn notifications off and on again.',
                result.delivered > 0 ? 'success' : 'error',
              );
            } catch {
              push('We couldn’t send a test right now.', 'error');
            } finally {
              setTesting(false);
            }
          }}
        >
          Send a test notification
        </Button>
      )}
    </div>
  );
}
